import type { StorefrontConfig, TemplateId } from './types';

/**
 * Demo content, one set per template.
 *
 * Each template is designed around a different kind of catalogue, so previewing
 * all three against the same three t-shirts would flatter the wrong one and
 * hide what actually distinguishes them — Market only looks right with a dense
 * repeat-purchase catalogue, Studio only with a handful of considered products.
 */

const BOUTIQUE: StorefrontConfig = {
  templateId: 'boutique',
  name: 'Aduke Studio',
  bio: 'Ready-to-wear made in Lagos, in small runs.',
  accent: '#16191D',
  domain: 'aduke.payiq.shop',
  category: 'Fashion & apparel',
  location: 'Victoria Island, Lagos',
  hours: 'Mon–Sat, 10am–7pm',
  showPrices: true,
  currency: '₦',
  announcement: 'Free delivery in Lagos on orders over ₦100,000',
  collections: ['New in', 'Ready to wear', 'Outerwear', 'Accessories'],
  products: [
    { id: 'b1', name: 'Oversized linen shirt', price: 48000, category: 'Ready to wear', swatch: ['#D8CFC4', '#9C9186'], badge: 'New' },
    { id: 'b2', name: 'Wide-leg trouser', price: 62000, compareAt: 74000, category: 'Ready to wear', swatch: ['#2E3237', '#15181B'] },
    { id: 'b3', name: 'Cropped utility jacket', price: 128000, category: 'Outerwear', swatch: ['#8A9384', '#5C6357'] },
    { id: 'b4', name: 'Silk scarf', price: 24000, category: 'Accessories', swatch: ['#C9A2A2', '#8E6A6A'] },
    { id: 'b5', name: 'Knit vest', price: 54000, category: 'Ready to wear', swatch: ['#E0D6C8', '#B3A695'], soldOut: true },
    { id: 'b6', name: 'Structured tote', price: 96000, category: 'Accessories', swatch: ['#6B5D52', '#3F3630'] },
  ],
};

const MARKET: StorefrontConfig = {
  templateId: 'market',
  name: 'Lagos Pantry',
  bio: 'Everyday groceries, delivered across Lagos in 24 hours.',
  accent: '#209347',
  domain: 'lagos-pantry.payiq.shop',
  category: 'Food & grocery',
  location: 'Ikeja, Lagos',
  hours: 'Daily, 7am–9pm',
  showPrices: true,
  currency: '₦',
  announcement: 'Order before 4pm for next-day delivery',
  collections: ['Staples', 'Sauces', 'Spices', 'Snacks', 'Drinks', 'Household'],
  products: [
    { id: 'm1', name: 'Ofada rice, 5kg', price: 12500, category: 'Staples', swatch: ['#E8DFC8', '#C3B48C'] },
    { id: 'm2', name: 'Palm oil, 2L', price: 7800, compareAt: 9000, category: 'Staples', swatch: ['#E4A34C', '#B9762A'], badge: 'Save ₦1,200' },
    { id: 'm3', name: 'Pepper mix, 500g', price: 4200, category: 'Spices', swatch: ['#C4462F', '#8C2C1C'] },
    { id: 'm4', name: 'Groundnut oil, 1L', price: 5600, category: 'Staples', swatch: ['#EBC978', '#C29A43'] },
    { id: 'm5', name: 'Plantain chips', price: 1800, category: 'Snacks', swatch: ['#F0D79B', '#C9A85F'] },
    { id: 'm6', name: 'Hibiscus drink, 1L', price: 2500, category: 'Drinks', swatch: ['#A32E4E', '#6D1B32'] },
    { id: 'm7', name: 'Beans, 2kg', price: 6900, category: 'Staples', swatch: ['#B8956A', '#856844'] },
    { id: 'm8', name: 'Dish soap, 750ml', price: 3200, category: 'Household', swatch: ['#5EA8C4', '#33748C'], soldOut: true },
  ],
};

const STUDIO: StorefrontConfig = {
  templateId: 'studio',
  name: 'Ori Skin',
  bio: 'Shea-based skincare formulated for African skin, made in small batches.',
  accent: '#8A6F52',
  domain: 'ori-skin.payiq.shop',
  category: 'Beauty & wellness',
  location: 'Lekki, Lagos',
  hours: 'Mon–Fri, 9am–5pm',
  showPrices: true,
  currency: '₦',
  announcement: 'Free consultation with every first order',
  collections: ['Cleanse', 'Treat', 'Moisturise', 'Protect'],
  products: [
    {
      id: 's1',
      name: 'Shea cleansing balm',
      price: 18500,
      category: 'Cleanse',
      blurb: 'Melts sunscreen and makeup without stripping. Unscented.',
      swatch: ['#E6D8C6', '#BFAA92'],
    },
    {
      id: 's2',
      name: 'Niacinamide serum',
      price: 26000,
      category: 'Treat',
      blurb: 'Evens tone and fades marks over 8–12 weeks of nightly use.',
      swatch: ['#D9C7B4', '#A88E74'],
      badge: 'Bestseller',
    },
    {
      id: 's3',
      name: 'Whipped body cream',
      price: 21000,
      category: 'Moisturise',
      blurb: 'Unrefined shea whipped light enough to absorb in a minute.',
      swatch: ['#EFE4D6', '#C9B69F'],
    },
    {
      id: 's4',
      name: 'Mineral SPF 40',
      price: 32000,
      category: 'Protect',
      blurb: 'Zinc-based, tinted in three shades, no white cast.',
      swatch: ['#C7B29A', '#957E66'],
    },
  ],
};

export const DEMO_CONFIGS: Record<TemplateId, StorefrontConfig> = {
  boutique: BOUTIQUE,
  market: MARKET,
  studio: STUDIO,
};

export const TEMPLATE_META: {
  id: TemplateId;
  name: string;
  category: string;
  rationale: string;
}[] = [
  {
    id: 'boutique',
    name: 'Boutique',
    category: 'Fashion & apparel',
    rationale:
      'Photography carries the sale, so the chrome gets out of the way: full-bleed hero, oversized type, a sparse grid and almost no UI.',
  },
  {
    id: 'market',
    name: 'Market',
    category: 'Food & grocery',
    rationale:
      'Repeat baskets, not browsing. Categories first, delivery promise up top, dense grid with prices and quick add on every tile.',
  },
  {
    id: 'studio',
    name: 'Studio',
    category: 'Beauty & wellness',
    rationale:
      'Few products, each needing an argument. Editorial layout with room for what it does, who it is for, and proof.',
  },
];
